import { getShellToken } from '../utils/shellToken';

function authHeaders(json = true) {
  const token = getShellToken();
  const h = {};
  if (token) h.Authorization = `Bearer ${token}`;
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

async function fitLiveFetch(path, options = {}) {
  const isForm = options.body instanceof FormData;
  const res = await fetch(`/api/fit-live${path}`, {
    ...options,
    headers: { ...authHeaders(!isForm), ...(options.headers || {}) },
  });
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json') || ct.includes('text/csv')) {
    if (ct.includes('text/csv')) {
      const text = await res.text();
      if (!res.ok) throw new Error('csv_error');
      return text;
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.error || `http_${res.status}`);
      err.status = res.status;
      err.payload = data;
      throw err;
    }
    return data;
  }
  if (!res.ok) throw new Error(`http_${res.status}`);
  return res.blob();
}

export const fitLiveApi = {
  snapshot: () => fitLiveFetch('/metrics/snapshot'),
  meta: () => fitLiveFetch('/meta'),
  me: () => fitLiveFetch('/creators/me'),
  creatorHomeStats: ({ from, to } = {}) => {
    const q = new URLSearchParams();
    if (from) q.set('from', from);
    if (to) q.set('to', to);
    const qs = q.toString();
    return fitLiveFetch(`/creators/me/home-stats${qs ? `?${qs}` : ''}`);
  },
  listPublished: () => fitLiveFetch('/classes?scope=published'),
  listMine: () => fitLiveFetch('/classes?scope=mine'),
  createClass: (body) => fitLiveFetch('/classes', { method: 'POST', body: JSON.stringify(body) }),
  classImage: (id, file) => {
    const fd = new FormData();
    fd.append('image', file);
    return fitLiveFetch(`/classes/${id}/image`, { method: 'POST', body: fd });
  },
  publish: (id) => fitLiveFetch(`/classes/${id}/publish`, { method: 'POST', body: '{}' }),
  unpublish: (id) => fitLiveFetch(`/classes/${id}/unpublish`, { method: 'POST', body: '{}' }),
  downloadTemplate: async () => {
    const blob = await fitLiveFetch('/import/template');
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'fit-live-plantilla-clases.xlsx';
    a.click();
    URL.revokeObjectURL(url);
  },
  importValidate: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return fitLiveFetch('/import/validate', { method: 'POST', body: fd });
  },
  importPatchRow: (sessionId, excelRow, body) => fitLiveFetch(
    `/import/${sessionId}/rows/${excelRow}`,
    { method: 'PATCH', body: JSON.stringify(body) },
  ),
  importRevalidate: (sessionId) => fitLiveFetch(`/import/${sessionId}/revalidate`, { method: 'POST', body: '{}' }),
  importPublish: (sessionId) => fitLiveFetch(`/import/${sessionId}/publish`, {
    method: 'POST',
    body: JSON.stringify({ include_warnings: true }),
  }),
  walletMe: () => fitLiveFetch('/wallet/me'),
  walletLedger: () => fitLiveFetch('/wallet/ledger'),
  packages: () => fitLiveFetch('/packages'),
  purchaseTest: (id) => fitLiveFetch(`/packages/${id}/purchase-test`, { method: 'POST', body: '{}' }),
  checkoutWompi: (id) => fitLiveFetch(`/packages/${id}/checkout-wompi`, { method: 'POST', body: '{}' }),
  grantBonus: (body) => fitLiveFetch('/wallet/bonus', { method: 'POST', body: JSON.stringify(body) }),
  masterPurchaseConfirm: (id) => fitLiveFetch(`/master/purchases/${encodeURIComponent(id)}/confirm`, {
    method: 'POST',
    body: '{}',
  }),
  economy: () => fitLiveFetch('/economy'),
  patchEconomy: (body) => fitLiveFetch('/economy', { method: 'PATCH', body: JSON.stringify(body) }),
  publicCatalog: (discipline = 'all') => fetch(`/api/fit-live/public/catalog?discipline=${encodeURIComponent(discipline)}`).then((r) => r.json()),
  reserve: (classId) => fitLiveFetch('/reservations', { method: 'POST', body: JSON.stringify({ class_id: classId }) }),
  myReservations: () => fitLiveFetch('/reservations/mine'),
  cancelReservation: (id) => fitLiveFetch(`/reservations/${id}/cancel`, { method: 'POST', body: '{}' }),
  attend: (id, present = true) => fitLiveFetch(`/reservations/${id}/attend`, { method: 'POST', body: JSON.stringify({ present }) }),
  rate: (classId, stars, comment = '') => fitLiveFetch(`/classes/${classId}/rate`, { method: 'POST', body: JSON.stringify({ stars, comment }) }),
  watchReplay: (id) => fitLiveFetch(`/replays/${id}/watch`, { method: 'POST', body: '{}' }),
  masterReplays: (creatorId) => {
    const q = creatorId ? `?creator_id=${encodeURIComponent(creatorId)}` : '';
    return fitLiveFetch(`/master/replays${q}`);
  },
  tip: (classId, tokens, message = '') => fitLiveFetch(`/classes/${classId}/tip`, { method: 'POST', body: JSON.stringify({ tokens, message }) }),
  masterGet: () => fitLiveFetch('/master'),
  masterPatch: (body) => fitLiveFetch('/master', { method: 'PATCH', body: JSON.stringify(body) }),
  masterCreatorCreate: (body) => fitLiveFetch('/master/creators', { method: 'POST', body: JSON.stringify(body) }),
  masterCreatorPatch: (id, body) => fitLiveFetch(`/master/creators/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  masterCreatorAvatar: (id, file) => {
    const fd = new FormData();
    fd.append('avatar', file);
    return fitLiveFetch(`/master/creators/${id}/avatar`, { method: 'POST', body: fd });
  },
  masterUsers: () => fitLiveFetch('/master/users'),
  masterAthleteCreate: (body) => fitLiveFetch('/master/athletes', { method: 'POST', body: JSON.stringify(body) }),
  masterAthletePatch: (userId, body) => fitLiveFetch(`/master/athletes/${userId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  masterAthleteDelete: (userId) => fitLiveFetch(`/master/athletes/${userId}`, { method: 'DELETE' }),
  masterCreatorDelete: (id) => fitLiveFetch(`/master/creators/${id}`, { method: 'DELETE' }),
  expireWelcomeDue: () => fitLiveFetch('/economy/welcome/expire-due', { method: 'POST', body: '{}' }),
  creatorMePatch: (body) => fitLiveFetch('/creators/me', { method: 'PATCH', body: JSON.stringify(body) }),
  attendance: (creatorId) => {
    const q = creatorId ? `?creator_id=${encodeURIComponent(creatorId)}` : '';
    return fitLiveFetch(`/attendance${q}`);
  },
  attendanceCsv: async () => {
    const token = getShellToken();
    const res = await fetch('/api/fit-live/attendance.csv', {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error('csv_error');
    return res.text();
  },
  processReminders: () => fitLiveFetch('/notifications/process-due', { method: 'POST', body: '{}' }),
  payouts: () => fitLiveFetch('/payouts'),
  payoutsAccrue: (period = '') => fitLiveFetch('/payouts/accrue', { method: 'POST', body: JSON.stringify({ period }) }),
  payoutMarkPaid: (id) => fitLiveFetch(`/payouts/${id}/mark-paid`, { method: 'POST', body: '{}' }),
  betaReadiness: () => fitLiveFetch('/beta/readiness'),
  betaSeed: () => fitLiveFetch('/beta/seed-creators', { method: 'POST', body: '{}' }),
  liveStatus: (classId, status) => fitLiveFetch(`/classes/${classId}/live-status`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  }),
};

const DEFAULT_CLASS_IMAGE = 'https://images.unsplash.com/photo-1518611012118-696072aa579a?auto=format&fit=crop&w=900&q=80';

export function mapClassToCard(row, { creatorAvatar } = {}) {
  const levelLabel = String(row.level || '').replace(/^\w/, (c) => c.toUpperCase());
  const disc = String(row.discipline || '');
  const image = row.image_url || creatorAvatar || DEFAULT_CLASS_IMAGE;
  const freeReplay = row.free_replay === true || Number(row.price_tokens) === 0;
  return {
    id: row.id,
    title: row.title,
    discipline: disc.charAt(0).toUpperCase() + disc.slice(1),
    level: levelLabel,
    duration: row.duration_min,
    coach: row.creator_name || 'Creator',
    focus: row.focus || '',
    tokens: freeReplay ? 0 : row.price_tokens,
    freeReplay,
    tipo: row.tipo || 'vivo',
    image,
    status: row.status,
    when: row.fecha && row.hora ? `${row.fecha} · ${row.hora}` : (row.status === 'published' ? 'Publicada' : 'Borrador'),
  };
}
